import React, { useState, useEffect } from 'react';
import ExitIntentModal from './components/ExitIntentModal';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import Features from './components/Features';
import FreeTools from './components/FreeTools';
import QuizContainer from './components/QuizContainer';
import ResultsSection from './components/ResultsSection';
import FAQ from './components/FAQ';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import ContactModal from './components/ContactModal';
import { calculateResults } from './utils/calculator';
import { Question, Answer, UserType, Result } from './types';
import { useAnalytics } from './hooks/useAnalytics';
import questionsData from './data/questions';

function App() {
  const { trackEvent, trackConversion } = useAnalytics();
  const [currentSection, setCurrentSection] = useState<'intro' | 'quiz' | 'results'>('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [results, setResults] = useState<Result | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [questions] = useState<Question[]>(questionsData);

  const selectUserType = (type: UserType) => {
    setUserType(type);
    trackEvent({
      action: 'select_user_type',
      category: 'User Profile',
      label: type
    });
  };

  const startQuiz = () => {
    setCurrentSection('quiz');
    trackEvent({
      action: 'start_quiz',
      category: 'Quiz',
      label: userType
    });
  };

  const handleAnswer = (questionId: number, answer: Answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    trackEvent({
      action: 'answer_question',
      category: 'Quiz',
      label: `Q${questionId}`,
      value: currentQuestion + 1
    });
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      trackEvent({
        action: 'next_question',
        category: 'Quiz Navigation',
        value: currentQuestion + 2
      });
    } else {
      showEmailCapture();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      trackEvent({
        action: 'prev_question',
        category: 'Quiz Navigation',
        value: currentQuestion
      });
    }
  };

  const showEmailCapture = () => {
    setShowModal(true);
    trackEvent({
      action: 'show_email_capture',
      category: 'Lead Generation',
      label: userType
    });
  };

  const submitContact = async (email: string) => {
    const calculatedResults = calculateResults(answers, questions);
    
    // Get labels from questions
    const getQuestionLabel = (questionId: number, answerId: string | string[]) => {
      const question = questions.find(q => q.id === questionId);
      if (!question) return '';
      
      if (Array.isArray(answerId)) {
        return answerId
          .map(id => question.options.find(opt => opt.id === id)?.label || '')
          .filter(Boolean)
          .join(';');
      }
      
      return question.options.find(opt => opt.id === answerId)?.label || '';
    };
    
    const userTypeMap = {
      analyst: 'Analista',
      manager: 'Gestor'
    };

    if (window._hsq) {
      try {
        window._hsq.push(['setPath', '/quiz-completed']);

        const contactData = {
          email: email,
          calculadora___perfil: userTypeMap[userType as UserType] || '',
          jobtitle: getQuestionLabel(1, answers[1] as string),
          calculadora___volume_de_notas: getQuestionLabel(2, answers[2] as string),
          calculadora___frequencia_de_correcao_nfse: getQuestionLabel(3, answers[3] as string),
          calculadora___quantidade_de_sistemas_envolvidos: getQuestionLabel(4, answers[4] as string),
          calculadora___quantidade_de_prefeituras: getQuestionLabel(5, answers[5] as string),
          calculadora___frequencia_da_pressao_em_prazos: getQuestionLabel(6, answers[6] as string),
          calculadora___tempo_de_alinhamento_com_contabilidade: getQuestionLabel(7, answers[7] as string),
          calculadora___impacto_dos_erros_fiscais: getQuestionLabel(9, answers[9] as string),
          calculadora___atividades_manuais: getQuestionLabel(8, answers[8] as string[]),
          calculadora___o_que_faria_com_mais_tempo_: getQuestionLabel(10, answers[10] as string[]),
          calculadora___nota_diagnostico: calculatedResults.score,
          calculadora___horas_economizadas__semana_: calculatedResults.savings.weeklyHours,
          calculadora___economia_mensal: calculatedResults.savings.monthlyAmount.replace('R$ ', '').replace('.', ''),
          calculadora___economia_anual: calculatedResults.savings.annualAmount.replace('R$ ', '').replace('.', ''),
          calculadora___meses_para_payback__roi_: parseInt(calculatedResults.savings.roiPeriod)
        };
        
        window._hsq.push(['identify', contactData]);
        window._hsq.push(['trackPageView']);
        
        setTimeout(() => {
          window._hsq.push(['trackCustomBehavioralEvent', {
            name: 'pe_quiz_completed',
            properties: {
              score: calculatedResults.score,
              user_type: userTypeMap[userType as UserType] || '',
              potential_savings: calculatedResults.savings.monthlyAmount
            }
          }]);
        }, 500);
      } catch (error) {
        console.error('Error sending data to HubSpot:', error);
      }
    }

    // Push lead capture event to GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_capture',
        userType: userType,
        email: email,
        quizScore: calculatedResults.score
      });
    }
    
    // Update UI state
    setShowModal(false);
    setResults(calculatedResults);
    setCurrentSection('results');
    trackConversion('lead');
    
    trackEvent({
      action: 'view_results',
      category: 'Quiz',
      label: userType,
      value: calculatedResults.score
    });
  };

  const scheduleDemo = () => {
    setShowModal(true);
    trackConversion('demo');
    trackEvent({
      action: 'schedule_demo',
      category: 'Conversion',
      label: userType,
      value: results?.score
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-safe-top">
        {currentSection === 'intro' && (
          <>
            <HeroSection 
              userType={userType}
              onUserTypeSelect={selectUserType}
              onStart={startQuiz}
            />
            <Features />
            <FreeTools />
            <FAQ />
            <CTASection />
          </>
        )}
        
        {currentSection === 'quiz' && (
          <div id="quiz" className="py-12">
            <QuizContainer 
              questions={questions}
              currentQuestion={currentQuestion}
              answers={answers}
              onAnswer={handleAnswer}
              onNext={nextQuestion}
              onPrev={prevQuestion}
            />
          </div>
        )}
        
        {currentSection === 'results' && results && (
          <ResultsSection 
            results={results}
            onSchedule={scheduleDemo}
          />
        )}
      </main>
      
      <ContactModal 
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={submitContact}
        title={currentSection === 'results' ? 'Agende uma demonstração' : 'Quase lá!'}
        subtitle={currentSection === 'results' 
          ? 'Vamos mostrar como podemos automatizar sua operação fiscal' 
          : 'Digite seu email para receber o diagnóstico personalizado'}
      />
      
      <Footer />
    </div>
  );
}

export default App;